import { Component, computed, effect, inject, Input, signal } from '@angular/core';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ChildNotificationInterface, ItemCommunicationService, ParentNotificationInterface, ParentTriggerInterface } from 'src/app/features/items/components/items-module/items-tab/services/item-communication.service';
import { ItemGeneralFormGroups } from './item-general.model';
import { ItemGeneralFormService } from './item-general-form.service';
import { AlternativeTranslate, Categories, Departments, Items, Suppliers } from 'tilby-models';
import { MatFormFieldModule } from '@angular/material/form-field';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CommonModule, DecimalPipe } from '@angular/common';
import { MatInputModule } from '@angular/material/input';
import {MatSelectModule} from '@angular/material/select';
import { ItemDataCatalogService } from 'src/app/features/items/services/item-data-catalog.service';
import { color_items, EncodedData, units } from 'src/app/features/items/utils/item.constants';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import {MatChipInputEvent, MatChipsModule} from '@angular/material/chips';
import { MatIconModule } from '@angular/material/icon';
import { AlertDialogService } from 'src/app/dialogs';
import {MatAutocompleteModule} from '@angular/material/autocomplete';
import { filter, map, merge, Observable, of, startWith, Subject, takeUntil } from 'rxjs';
import { PriceListInterface } from 'src/app/features/items/utils/item.interface';
import { MathUtils } from 'src/app/shared/utils';
import { TilbyCurrencyPipe } from '@tilby/tilby-ui-lib/pipes/tilby-currency';
import { ItemDataOperationsService, TranslationsInputData } from 'src/app/features/items/services/item-data-operations.service';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { TruncatedTextDirective } from 'src/app/directives/truncated-text.directive';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatButtonModule } from '@angular/material/button';
import { BarcodeManagerService, ConfigurationManagerService, EnvironmentInfoService, LoaderService } from 'src/app/core';
import { BreakpointObserver } from '@angular/cdk/layout';
import { toSignal } from '@angular/core/rxjs-interop';
import { TrimInputDirective } from 'src/app/directives/trim.directive';
import { ItemStockFormService } from '../item-stock/item-stock-form.service';
import { TilbyDatePipe } from '@tilby/tilby-ui-lib/pipes/tilby-date';
import { MatTableModule } from '@angular/material/table';
import { BarcodeSelectorComponent } from '../barcode-selector/barcode-selector.component';

@Component({
  selector: 'app-item-general',
  standalone: true,
  imports: [ MatFormFieldModule, ReactiveFormsModule,
    TranslateModule, FormsModule, CommonModule,
    MatInputModule, MatSelectModule, MatSlideToggleModule,
    MatChipsModule, MatIconModule, MatAutocompleteModule,
    MatExpansionModule, MatCheckboxModule,
    TruncatedTextDirective, MatTooltipModule, MatButtonModule,
    TrimInputDirective, TilbyCurrencyPipe, TilbyDatePipe,
    MatTableModule, BarcodeSelectorComponent
  ],
  providers: [
    DecimalPipe
  ],
  templateUrl: './item-general.component.html',
  styleUrl: './item-general.component.scss'
})
export class ItemGeneralComponent {

  readonly itemGeneralFormService = inject(ItemGeneralFormService);
  readonly itemDataOperationsService = inject(ItemDataOperationsService);
  private readonly itemCommunicationService = inject(ItemCommunicationService);
  private readonly itemDataCatalogService = inject(ItemDataCatalogService);
  private readonly alertDialogService = inject(AlertDialogService);
  private readonly translate = inject(TranslateService);
  private readonly decimalPipe = inject(DecimalPipe);
  private readonly tilbyCurrencyPipe = inject(TilbyCurrencyPipe);
  private readonly configurationManager = inject(ConfigurationManagerService);
  private readonly itemStockFormService = inject(ItemStockFormService);
  private readonly loaderService = inject(LoaderService);
  private readonly environmentInfoService = inject(EnvironmentInfoService);
  private readonly barcodeManagerService = inject(BarcodeManagerService);
  private readonly configurationManagerService = inject(ConfigurationManagerService);
  
  @Input() item!: Items;

  itemGeneralForm!: FormGroup<ItemGeneralFormGroups>;

  categoriesList!: Array<Categories> | undefined;
  colorItems!: Array<EncodedData> | undefined;
  suppliersList!: Array<Suppliers>;
  priceList: Array<PriceListInterface> = [];
  departmentsList: Array<Departments> = [];
  departmentsRequiredList: Departments[] = [];
  units: Array<EncodedData> = [];
  itemLastMovements: Array<any> = [];

  protected displayedColumnsLastMovementsTable: string[] = ['price', 'supplier', 'date'];
  protected filteredSuppliers: Observable<Suppliers[] | undefined> | undefined = of([]);
  protected isAlternativeTranslateEnabled = !!this.configurationManager.getPreference('items.enable_alternative_translate');

  filteredUnits: Observable<EncodedData[]> | undefined = of([]);
  private destroy$ = new Subject<void>(); 

  isPienissimoEnabled = !!this.configurationManager.getPreference('pienissimo.token');
  protected isCameraBarcodeSupported = this.environmentInfoService.isCameraBarcodeSupported();

  showMoreDetails = false;
  showMorePrinters = false;

  private breakpointObserver = inject(BreakpointObserver);
  isLargeScreen = toSignal(
    this.breakpointObserver.observe('(min-width: 1024px)').pipe(
      map(result => result.matches)
    )
  );
  initialPriceLimit = computed(() =>this.isLargeScreen() ? 2 : 1);

  isDataListReady = this.itemDataCatalogService.isReady;
  itemGeneralFormReady = this.itemGeneralFormService.itemGeneralFormReady;
  private pendingStockRequest = signal(false);
  protected useNewBarcodeQty = this.configurationManagerService.getPreference('items.use_new_barcode_qty');

  constructor() {
    this.loaderService.show();
    effect(() => {
      if(this.isDataListReady()) {
        this.readData();
        this.itemGeneralForm = this.itemGeneralFormService.createForm(this.item);
        this.loaderService.hide();
        this.calculateDerivatedProps();
        this.itemGeneralFormService.updateCategoryField();
        this.listenToSupplierSearch();
        this.listenToUnitSearch();
        this.listenToPriceCostChanges();
      }
    }, { allowSignalWrites: true })

    effect(() => {
      if(this.itemGeneralFormService.itemDataGeneralFormReady()) {
        this.readItemLastMovements()
      }
    }, { allowSignalWrites: true })

    effect(() => {
      // Listener per eseguire operazioni richieste dal parent
      const trigger = this.itemCommunicationService.parentTrigger();
      if(trigger?.child === 'general' || trigger?.child === 'all') {
        this.handleParentTrigger(trigger);
      }
    }, { allowSignalWrites: true })

    effect(() => {
      // Listener per leggere notifiche inviate dal parent
      const notification = this.itemCommunicationService.parentNotifier();
      if (notification?.child === 'general') {
        this.handleParentNotification(notification);
      }
    })

    effect(() => {
      const isPending = this.pendingStockRequest();
      const isStockReady = this.itemStockFormService.stockFormReady();

      if (isPending && isStockReady) {
        this.itemCommunicationService.createParentTrigger(
          'requestStockData', 
          'stock', 
          'general', 
          { formField: 'stock_type' }
        );
        this.pendingStockRequest.set(false);
      }
    }, { allowSignalWrites: true });
  }

  handleParentTrigger(message: ParentTriggerInterface) {
    if(message.trigger === 'tabActivated') {
      this.pendingStockRequest.set(true);
    }

    if (message.trigger === 'requestGeneralData') {
      const requestedField = message.params?.formField;
      const requestedFieldsList = message.params?.fieldsList;

      let dataToReturn = null;
      let params: Record<string, any> = {};
      if (requestedField && requestedField === 'category' && this.itemGeneralForm.get('detailsForm.'+requestedField)) {
        dataToReturn = this.itemGeneralForm.get('detailsForm.'+requestedField)?.value;
        params = { [requestedField]: dataToReturn }
      }

      if (requestedFieldsList?.length) {
        requestedFieldsList.forEach((field: string) => {
          if (!field) return;

          // Cerco il valore nei vari form
          const detailsControl = this.itemGeneralForm.get('detailsForm.' + field);
          const pricesControl = this.itemGeneralForm.get('pricesForm.' + field);
          const costsControl = this.itemGeneralForm.get('costsForm.' + field);

          if (detailsControl) {
              params[field] = detailsControl.value;
          } else if (pricesControl) {
              params[field] = pricesControl.value;
          } else if (costsControl) {
              params[field] = costsControl.value;
          } else {
              params[field] = null;
          }
        });
      }

      this.sendNotificationToParent({
        notification: 'generalData',
        receiver: message.sender,
        params: params
      });
    } 
    
    if(message.trigger === 'cancel') {
      this.itemGeneralFormService.resetFormValues(this.item);
    }
  }

  handleParentNotification(message: ParentNotificationInterface) {
    if(message.notification === 'stockData') {
      if(message.params?.stock_type && message.params?.stock_type === 'bom') {
        this.itemGeneralFormService.cost?.disable();
      } else this.itemGeneralFormService.cost?.enable();
    }
    if(message.notification === 'stockCost') {
      this.itemGeneralFormService.cost?.setValue(message.params || null);
    }
  }

  listenToPriceCostChanges() {
    const price1Changes$ = this.itemGeneralForm.get('pricesForm.price1')!.valueChanges.pipe(
      map(value => ({ 
        field: 'price1', 
        group: 'pricesForm', 
        value 
      }))
    );

    const costChanges$ = this.itemGeneralForm.get('costsForm.cost')!.valueChanges.pipe(
      map(value => ({ 
        field: 'cost', 
        group: 'costsForm', 
        value 
      }))
    );

    merge(price1Changes$, costChanges$)
      .pipe(takeUntil(this.destroy$)).subscribe(event => {
      this.calculateDerivatedProps();
    });
  }

  readData() {
    // Costruzione lista di categorie
    this.categoriesList = this.itemDataCatalogService.getExtendedCategories() || [];

    // Costruzione lista di colori
    this.colorItems = [...color_items];
    this.colorItems.unshift({name: 'Nessuno', value: ''});

    // Lettura liste fornitori
    this.suppliersList = this.itemDataCatalogService.getDataByKey("suppliers") || [];

    // Costruzione liste di prezzi
    this.priceList = this.itemDataOperationsService.getPriceLists();

    // Costruzione lista di reparti
    this.departmentsList = this.itemDataCatalogService.getExtendedDepartments() || [];
    this.departmentsRequiredList = this.departmentsList.filter(d => d.id !== 0);

    // Costruzione lista di unità
    this.units = units.map(u => ({
      name: this.translate.instant(u.name),
      value: this.translate.instant(u.value)
    }))
  }

  readItemLastMovements() {
    // Lettura ultimi movimenti
    this.itemLastMovements = this.itemDataCatalogService.getDataByKey("item-last-movements") || [];
  }

  calculateDerivatedProps() {
    this.itemGeneralFormService.itemMargin?.setValue(this.calculateItemMargin());
    this.itemGeneralFormService.itemMarkup?.setValue(this.calculateItemMarkup());
  }

  calculateItemMargin() {
    const price1 = this.itemGeneralFormService.getPrice('price1')?.value;
    const cost = this.itemGeneralFormService.cost?.value;
    const departmentId = this.itemGeneralFormService.getPrice('department_id')?.value;

    if (price1 != null && cost != null && departmentId != null) {
      const itemDep = this.departmentsList?.find(dep => dep.id === departmentId);
      const vatPerc = itemDep?.vat?.value ?? 0;

      if (typeof price1 !== 'number' || typeof vatPerc !== 'number') {
        return '';
      }

      const netPrice = MathUtils.round(price1 / (1 + vatPerc / 100));


      if (typeof netPrice !== 'number' || typeof cost !== 'number') {
        return '';
      }

      const margin = netPrice - cost;
      const formattedMargin = this.tilbyCurrencyPipe.transform(margin);
      
      let percentage = (margin / netPrice) * 100;
      if(isNaN(percentage) || !isFinite(percentage)) {
        percentage = 0;
      }

      const formattedPercentage = this.decimalPipe.transform(percentage, '1.2-2');

      return `${formattedMargin} (${formattedPercentage}%)`;
    } else {
        return '';
    }
  }

  calculateItemMarkup() {
    const price1 = this.itemGeneralFormService.getPrice('price1')?.value;
    const cost = this.itemGeneralFormService.cost?.value;

    if (price1 != null && cost != null) {

      if (typeof price1 !== 'number' || typeof cost !== 'number') {
        return '';
      }
      const margin = price1 - cost;

      const formattedResult = this.decimalPipe.transform((margin / cost) * 100, '1.2-2');
      return `${formattedResult}%`;
    }
    else {
        return '';
    }
  }
  
  getSelectedColorName() {
    const colorName = this.colorItems?.find(col => col.value == this.itemGeneralFormService.color?.value)?.name;
    return colorName? this.translate.instant(colorName) : '';
  }

  removeBarcode(barcode: string) {
    const index = this.itemGeneralFormService.barcodes?.value.findIndex((ele: any) => ele.barcode === barcode);
    if (index !== undefined && index >= 0) {
      const currentValue = this.itemGeneralFormService.barcodes?.value || [];
      const updatedList = currentValue.filter((_: any, i: number) => i !== index);
      this.itemGeneralFormService.barcodes?.setValue(updatedList);
    }
  }

  addBarcodeFromChip(event: MatChipInputEvent): void {
    const value = (event.value || '').trim();
    this.addBarcode(value);

    // Clear the input value
    event.chipInput!.clear();
  }

  protected async openBarcodeAndSearch() {
    const barcode = await this.barcodeManagerService.openCameraBarcode();
    this.addBarcode(barcode);
  }

  addBarcode(value: string) {
    if (value) {
      const index = this.itemGeneralFormService.barcodes?.value.findIndex((ele: any) => ele.barcode === value);
      if (index !== undefined && index >= 0) {
        this.alertDialogService.openDialog({
          data: {
            messageLabel: this.translate.instant('ITEMS.DETAILS.BARCODE_ALREADY_PRESENT')
        }});
      } else {
        const currentValue = this.itemGeneralFormService.barcodes?.value || [];
        this.itemGeneralFormService.barcodes?.setValue([...currentValue, {barcode: value}]);
      }
    }
  }
  
  displaySupplierName = (value?: Suppliers | number): string => {
    let name = '';
    if(typeof value === 'object') name = value?.name;
    else if(typeof value === 'number') {
      const sup = this.suppliersList.find(sup => sup.id === value);
      if(sup) name = sup.name;
    }
    return name;
  }

  listenToSupplierSearch() {
    this.filteredSuppliers = this.itemGeneralFormService.default_supplier_id?.valueChanges.pipe(
        startWith(this.itemGeneralFormService.default_supplier_id?.value || null),
        map((value: any) => 
          this._filterSuppliers(value)
        )
    );
  }

  private _filterSuppliers(value: string | number): Suppliers[] {
    if(!this.suppliersList) {
      return [];
    }

    if(typeof value === 'number') {
      const supplier = this.suppliersList.find(sup => sup.id === value);
      return supplier? [supplier]: [];
    }

    const filterValue = typeof value === 'string'? value.toLocaleLowerCase().trim(): '';
    return this.suppliersList?.filter(option => option.name.toLocaleLowerCase().includes(filterValue));
  }

  listenToUnitSearch() {
    this.filteredUnits = this.itemGeneralFormService.unit?.valueChanges.pipe(
      startWith(null),
      filter(value => typeof value === 'string' || value === null),
      map(value => (value ? this._filterUnit(value) : this.units?.slice() || []))
    );
  }

  private _filterUnit(value: string): EncodedData[] {
    const filterValue = value.toLowerCase();
    return this.units.filter(option => option.name.toLowerCase().includes(filterValue));
  }

  displayUnit(unit: EncodedData | string): string {
    return typeof unit === 'string'? unit: unit?.name? unit.name: '';
  }

  sendNotificationToParent(notification: ChildNotificationInterface) {
    Promise.resolve().then(() => {
      this.itemCommunicationService.notifyParent(notification);
    });
  }

  onChangeCategory() {
    this.itemGeneralFormService.updateCategoryField();
  }

  manageAlternativeTranslate() {
    const config: TranslationsInputData = {
      mainValue: this.itemGeneralFormService.name?.value,
      currentValues: this.itemGeneralFormService.alternativeNames?.value || [],
      maxLength: this.itemGeneralFormService.validators.nameMaxLength
    }
    this.itemDataOperationsService.getUpdatedTranslations(config).then((updatedValues: AlternativeTranslate[] | null) => {
      if(updatedValues) this.itemGeneralFormService.alternativeNames?.setValue(updatedValues);
    })
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    this.itemGeneralFormReady.set(false);
  }
}
